import React from 'react';
import { useState } from 'react';
import { Bot, Send, Globe, Database, Code2, ChevronDown } from 'lucide-react';

const FeaturesSection = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggleExpand = React.useCallback((index: number) => {
    setExpandedIndex(prev => prev === index ? null : index);
  }, []);

  const features = [
    {
      icon: Bot,
      title: "AI Chatbots & Customer Support",
      description: "Answers enquiries on your website and by message, day or night, so leads don't go cold while you're busy working.",
      detail: "Answers questions instantly, captures enquiry details, and hands them to you — so nobody waits until morning for a reply. Handles FAQs, booking requests, and quote forms without you touching it.",
      gradient: "from-purple-500 via-blue-500 to-emerald-400",
      bgGradient: "from-purple-500/20 via-blue-500/10 to-emerald-400/20",
      hoverBorder: "hover:border-blue-500/30"
    },
    {
      icon: Send,
      title: "Automated Outreach",
      description: "Finds and contacts the right decision-makers on your behalf, so your pipeline keeps filling without you working evenings. We run this ourselves in our own construction company — automated outreach to architects and off the back of planning applications.",
      detail: "We identify the people who commission work, send personalised messages on your behalf, and follow up automatically. You get qualified conversations handed to you, not just data.",
      gradient: "from-blue-500 via-purple-600 to-emerald-400",
      bgGradient: "from-blue-500/20 via-purple-600/10 to-emerald-400/20",
      hoverBorder: "hover:border-purple-500/30"
    },
    {
      icon: Globe,
      title: "Websites & Ad Campaigns",
      description: "Sites built to bring work in rather than just sit there, plus targeted advertising managed for you.",
      detail: "A proper website that loads fast, looks good on a phone, and turns visitors into enquiries. We handle the ads too — so you're not wasting money boosting posts and hoping.",
      gradient: "from-emerald-400 via-blue-500 to-yellow-400",
      bgGradient: "from-emerald-400/20 via-blue-500/10 to-yellow-400/20",
      hoverBorder: "hover:border-yellow-400/30"
    },
    {
      icon: Database,
      title: "CRM & Scheduling",
      description: "Every enquiry, quote and appointment in one place, updated automatically. Fewer no-shows, nothing lost in a text thread.",
      detail: "Enquiries go straight into a dashboard with automated reminders sent by text or email. Clients book their own slots, you see everything in one place, and nothing gets forgotten.",
      gradient: "from-emerald-400 via-blue-500 to-purple-600",
      bgGradient: "from-emerald-400/20 via-blue-500/10 to-purple-600/20",
      hoverBorder: "hover:border-emerald-400/30"
    },
    {
      icon: Code2,
      title: "Custom Software",
      description: "Bespoke tools built around how your firm actually works — not off-the-shelf software you have to bend to fit.",
      detail: "If you can describe the problem, we can build the tool. Dashboards, quoting tools, client portals, automated reporting — whatever you need, built for your business and owned by you.",
      gradient: "from-blue-500 via-purple-600 to-red-400",
      bgGradient: "from-blue-500/20 via-purple-600/10 to-red-400/20",
      hoverBorder: "hover:border-red-400/30"
    }
  ];

  return (
    <section id="solutions" className="py-16 sm:py-20 px-4 sm:px-6 bg-black md:bg-black bg-gradient-to-br from-purple-900/20 via-blue-900/10 to-gray-900/20 tech-grid" aria-labelledby="solutions-heading">
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 id="solutions-heading" className="text-5xl sm:text-5xl md:text-6xl font-display text-white mb-8 sm:mb-8 tracking-wide px-2">
            Our{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-gray-300">
              AI Solutions
            </span>
          </h2>
          <p className="text-xl sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-tech px-4 mb-6 sm:mb-4">
            Five things we build for small businesses
          </p>
          <p className="text-lg sm:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed font-tech px-4">
            Each one designed to win you more work and save you time
          </p>
        </div>

        {/* Features Grid — 3 cols on desktop (last row centred), 2 on tablet, 1 on mobile */}
        <div className="grid grid-cols-1 tab:grid-cols-2 desk:grid-cols-6 gap-8 mb-20 sm:mb-20" role="list">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={index}
                className={`${index === 3 ? 'desk:col-start-2' : ''} desk:col-span-2 group p-8 sm:p-10 rounded-3xl bg-gray-900/40 bg-gradient-to-br from-purple-800/30 via-blue-800/20 to-gray-800/30 border-2 border-gray-800/60 border-purple-500/40 ${feature.hoverBorder} transition-all duration-300 hover:bg-gray-900/60 hover:bg-gradient-to-br hover:from-purple-700/40 hover:via-blue-700/30 hover:to-gray-700/40 backdrop-blur-sm hover:scale-105 mobile-card-glow hover:shadow-2xl flex flex-col`}
                role="listitem"
              >
                <div className={`w-24 h-24 sm:w-24 sm:h-24 mb-10 sm:mb-10 bg-gradient-to-r ${feature.bgGradient} md:${feature.bgGradient} from-purple-500/40 via-blue-500/30 to-silver-500/40 rounded-3xl flex items-center justify-center group-hover:scale-125 transition-transform duration-300 mobile-card-glow shadow-lg`}>
                  <IconComponent className={`w-12 h-12 sm:w-12 sm:h-12 bg-gradient-to-r ${feature.gradient} md:${feature.gradient} from-purple-400 via-blue-400 to-silver-400 bg-clip-text text-transparent`} aria-hidden="true" />
                </div>
                
                <h3 className="text-xl sm:text-xl lg:text-2xl font-bold text-white md:text-white mobile-accent-white mb-4 sm:mb-4 font-robotic mobile-text-glow leading-tight">
                  {feature.title}
                </h3>
                
                <p className="text-base sm:text-base lg:text-lg text-gray-300 md:text-gray-300 mobile-accent-silver leading-relaxed mb-6 sm:mb-8 font-tech flex-grow">
                  {feature.description}
                </p>

                {expandedIndex === index && (
                  <div className="mb-4 p-4 rounded-xl bg-black/30 border border-gray-700/40">
                    <p className="text-sm sm:text-base text-gray-300 leading-relaxed font-tech">
                      {feature.detail}
                    </p>
                  </div>
                )}

                <button
                  onClick={() => handleToggleExpand(index)}
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm sm:text-base font-robotic text-white bg-gradient-to-r from-blue-600/80 to-purple-600/80 hover:from-blue-500 hover:to-purple-500 rounded-lg transition-all duration-300 hover:scale-105 active:scale-95 font-bold mt-auto shadow-lg hover:shadow-xl"
                >
                  <span>{expandedIndex === index ? 'Show less' : 'Learn More'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${expandedIndex === index ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <h3 className="text-3xl sm:text-3xl md:text-4xl font-bold text-white mb-8 sm:mb-6 font-robotic mobile-text-glow">
            Not sure what to automate first?
          </h3>
          <p className="text-xl sm:text-xl text-gray-300 mb-10 sm:mb-8 max-w-2xl mx-auto font-tech mobile-accent-blue">
            Most people start with the one that's costing them work — usually the enquiries that arrive when nobody's free to answer. Tell me what your week actually looks like and I'll tell you where the easy win is.
          </p>
          <a
            href="#our-work"
            className="group relative px-16 sm:px-16 py-6 sm:py-5 bg-gradient-to-r from-purple-600 to-gray-500 hover:from-purple-700 hover:to-gray-600 rounded-2xl text-white font-bold text-xl sm:text-xl tracking-wide transition-all duration-300 hover:scale-110 active:scale-95 hover:shadow-2xl hover:shadow-purple-600/40 cursor-pointer inline-block"
          >
            <span className="relative z-10 font-robotic">See Our Work</span>
          </a>
        </div>
      </div>

    </section>
  );
};

export default FeaturesSection;
